import { ESCALATION_LEVELS } from './types';

interface InvestigationPanelProps {
  resolution: any;
  detail: any;
  auditData: any;
  isResolved: boolean;
  exc: any;
  loadingResolution: boolean;
  onGenerateResolution: () => void;
}

export default function InvestigationPanel({
  resolution, detail, auditData, isResolved, exc,
  loadingResolution, onGenerateResolution,
}: InvestigationPanelProps) {
  // Metadata filter
  const isEngineMetadata = (text: string) =>
    /workflow skill:|Matching complete:|Detected by workflow|exceptions out of|records scanned/i.test(text);

  if (!resolution) {
    return (
      <div style={{ marginBottom: 16 }}>
        {loadingResolution ? (
          <div style={{ padding: 24, textAlign: 'center', background: 'rgba(0,0,0,0.02)', borderRadius: 8 }}>
            <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 8 }}>Generating AI Resolution...</div>
            <div style={{ fontSize: 10, color: 'var(--text-dimmer)' }}>Diagnosing root cause, calculating financial impact, generating options</div>
          </div>
        ) : !isResolved ? (
          <button onClick={onGenerateResolution} style={{
            width: '100%', padding: '14px 16px',
            background: 'linear-gradient(135deg, rgba(59,130,246,0.2), var(--accent-border))',
            border: '1px solid var(--accent-border)', borderRadius: 8, color: 'var(--text-primary)', fontWeight: 700, fontSize: 12, cursor: 'pointer',
          }}>
            GENERATE AI RESOLUTION
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <>
      {/* AI Narrative */}
      {resolution.diagnosis && (resolution.diagnosis.narrative || resolution.diagnosis.summary) && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dimmer)', letterSpacing: 0.5 }}>AI NARRATIVE</span>
            {resolution.resolution_mode === 'MODE_1_ENRICHED' && (
              <span style={{ fontSize: 8, padding: '2px 6px', background: 'var(--accent-subtle)', color: 'var(--navy-700)', borderRadius: 3, fontWeight: 700 }}>AI-Enhanced</span>
            )}
            {resolution.resolution_mode === 'MODE_2' && (
              <span style={{ fontSize: 8, padding: '2px 6px', background: 'var(--accent-subtle)', color: 'var(--navy-800)', borderRadius: 3, fontWeight: 700 }}>AI-Generated</span>
            )}
          </div>
          <div style={{ padding: 14, background: 'rgba(0,0,0,0.02)', borderRadius: 8, borderLeft: '3px solid var(--accent)' }}>
            <div style={{ fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.7 }}>
              {resolution.diagnosis.narrative || resolution.diagnosis.summary}
            </div>
          </div>
        </div>
      )}

      {/* Root Cause */}
      {(() => {
        const rc = resolution.diagnosis?.root_cause_detail || resolution.diagnosis?.root_cause;
        if (!rc || isEngineMetadata(rc)) return null;
        return (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dimmer)', marginBottom: 8, letterSpacing: 0.5 }}>ROOT CAUSE</div>
            <div style={{ padding: 14, background: 'rgba(0,0,0,0.02)', borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.6 }}>{rc}</div>
            </div>
          </div>
        );
      })()}

      {/* Account Impact */}
      {resolution.account_impact_analysis && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dimmer)', marginBottom: 8, letterSpacing: 0.5 }}>ACCOUNT IMPACT</div>
          <div style={{ padding: 14, background: 'rgba(0,0,0,0.02)', borderRadius: 8 }}>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.6 }}>{resolution.account_impact_analysis}</div>
          </div>
        </div>
      )}

      {/* Standards Applied -- tolerate both shapes the LLM returns:
            (a) string[]            ["ASC 606-10-25-1", "COSO/SOX"]
            (b) object[] with shape [{standard, section, citation}, ...]
          Prior code assumed (a) only and crashed with React error #31
          ("objects are not valid as a React child") when the LLM
          returned shape (b) -- which Albert hit on 2026-05-22. */}
      {resolution.standards_applied?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dimmer)', marginBottom: 8, letterSpacing: 0.5 }}>STANDARDS APPLIED</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {resolution.standards_applied.map((std: any, i: number) => {
              const label = typeof std === 'string'
                ? std
                : (std?.standard ?? std?.section ?? std?.citation ?? JSON.stringify(std));
              const tooltip = typeof std === 'string'
                ? undefined
                : [std?.section, std?.citation].filter(Boolean).join(' — ');
              return (
                <span key={i} title={tooltip || undefined} style={{
                  fontSize: 9, padding: '3px 8px',
                  background: 'var(--accent-subtle)', border: '1px solid var(--accent-border)',
                  borderRadius: 4, color: 'var(--navy-700)', fontWeight: 600,
                }}>
                  {label}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Financial Impact */}
      {resolution.financial_impact && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dimmer)', marginBottom: 8, letterSpacing: 0.5 }}>FINANCIAL IMPACT</div>
          <div style={{ padding: 14, background: 'rgba(0,0,0,0.02)', borderRadius: 8 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <div style={{ fontSize: 9, color: 'var(--accent-critical)', fontWeight: 700, marginBottom: 4 }}>IF UNRESOLVED</div>
                <div style={{ fontSize: 10, color: 'var(--negative)', lineHeight: 1.5 }}>{resolution.financial_impact.if_unresolved}</div>
              </div>
              <div>
                <div style={{ fontSize: 9, color: 'var(--accent-good)', fontWeight: 700, marginBottom: 4 }}>IF RESOLVED</div>
                <div style={{ fontSize: 10, color: 'var(--positive)', lineHeight: 1.5 }}>{resolution.financial_impact.if_resolved}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Audit Findings Panel */}
      {auditData && auditData.finding_count > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dimmer)', marginBottom: 8, letterSpacing: 0.5 }}>AUDIT FINDINGS</div>
          <div style={{
            padding: 14, borderRadius: 8,
            background: (auditData?.critical_count ?? 0) > 0 ? 'rgba(239,68,68,0.06)' : 'rgba(251,191,36,0.06)',
            border: `1px solid ${(auditData?.critical_count ?? 0) > 0 ? '#fecaca' : 'rgba(251,191,36,0.25)'}`,
          }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 10, alignItems: 'center' }}>
              <span style={{
                fontSize: 9, fontWeight: 600, letterSpacing: 0.5, padding: '3px 8px', borderRadius: 4,
                background: auditData.approve_allowed ? 'rgba(251,191,36,0.15)' : '#fef2f2',
                color: auditData.approve_allowed ? 'var(--warning)' : 'var(--negative)',
              }}>
                {auditData.approve_allowed ? 'REVIEW REQUIRED' : 'APPROVAL BLOCKED'}
              </span>
              {auditData.critical_count > 0 && (
                <span style={{ fontSize: 9, color: 'var(--negative)', fontWeight: 700 }}>
                  {auditData.critical_count} CRITICAL
                </span>
              )}
              {auditData.high_count > 0 && (
                <span style={{ fontSize: 9, color: 'var(--warning)', fontWeight: 700 }}>
                  {auditData.high_count} HIGH
                </span>
              )}
              {auditData.medium_count > 0 && (
                <span style={{ fontSize: 9, color: 'var(--text-dim)', fontWeight: 700 }}>
                  {auditData.medium_count} MEDIUM
                </span>
              )}
              {auditData.escalate_to && (
                <span style={{ fontSize: 9, color: 'var(--navy-800)', fontWeight: 700 }}>
                  REQUIRES {auditData.escalate_to} APPROVAL
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {(auditData.findings || []).map((f: any, i: number) => (
                <div key={i} style={{
                  display: 'flex', gap: 8, alignItems: 'flex-start',
                  padding: '4px 8px', borderRadius: 4,
                  background: f.severity === 'CRITICAL' ? 'rgba(239,68,68,0.05)' : 'transparent',
                }}>
                  <span style={{
                    fontSize: 8, fontWeight: 600, letterSpacing: 0.3, minWidth: 50, paddingTop: 1,
                    color: f.severity === 'CRITICAL' ? 'var(--negative)' : f.severity === 'HIGH' ? 'var(--warning)' : 'var(--text-dim)',
                  }}>
                    {f.severity}
                  </span>
                  <span style={{
                    fontSize: 8, fontWeight: 700, minWidth: 80, color: 'var(--text-dimmer)', paddingTop: 1,
                  }}>
                    {(f.check || '').replace(/_/g, ' ')}
                  </span>
                  <span style={{ fontSize: 9, color: 'var(--text-dim)', flex: 1 }}>
                    {f.option_id ? `[Opt ${f.option_id}] ` : ''}{f.message}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Corrective Actions */}
      {resolution.corrective_actions_detail && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dimmer)', marginBottom: 8, letterSpacing: 0.5 }}>CORRECTIVE ACTIONS</div>
          <div style={{ padding: 14, background: 'rgba(0,0,0,0.02)', borderRadius: 8 }}>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.6 }}>{resolution.corrective_actions_detail}</div>
          </div>
        </div>
      )}

      {/* Review History */}
      {detail?.review_chain?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dimmer)', marginBottom: 8, letterSpacing: 0.5 }}>REVIEW HISTORY</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {detail.review_chain.map((review: any, i: number) => {
              const levelCfg = ESCALATION_LEVELS[review.level] || { color: 'var(--text-muted)', bg: 'var(--bg-elevated)' };
              return (
                <div key={i} style={{
                  display: 'flex', gap: 8, alignItems: 'center',
                  padding: '6px 10px', background: 'rgba(0,0,0,0.02)',
                  border: '1px solid var(--bg-border)', borderRadius: 6,
                }}>
                  {review.level && (
                    <span style={{
                      fontSize: 8, fontWeight: 600, padding: '2px 6px',
                      background: levelCfg.bg, color: levelCfg.color,
                      borderRadius: 3, letterSpacing: 0.5, minWidth: 24, textAlign: 'center',
                    }}>{review.level}</span>
                  )}
                  <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-primary)', minWidth: 80 }}>
                    {review.agent_name}
                  </span>
                  <span style={{ fontSize: 9, color: 'var(--navy-800)', fontWeight: 600, minWidth: 60 }}>
                    {(review.action || '').replace(/_/g, ' ')}
                  </span>
                  <span style={{ fontSize: 9, color: 'var(--text-dim)', flex: 1 }}>
                    {review.comment}
                  </span>
                  <span style={{ fontSize: 8, color: 'var(--text-dimmer)', whiteSpace: 'nowrap' }}>
                    {review.timestamp ? new Date(review.timestamp).toLocaleString() : ''}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Follow-Up Tasks */}
      {resolution.follow_up_tasks?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dimmer)', marginBottom: 8, letterSpacing: 0.5 }}>FOLLOW-UP TASKS</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {resolution.follow_up_tasks.map((t: any, i: number) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px', background: 'rgba(251,191,36,0.04)',
                border: '1px solid rgba(251,191,36,0.15)', borderRadius: 6,
              }}>
                <span style={{ fontSize: 10, color: 'var(--text-primary)', flex: 1 }}>{t.task}</span>
                <span style={{ fontSize: 9, color: 'var(--text-dimmer)', marginLeft: 12, whiteSpace: 'nowrap' }}>
                  {t.owner}{(t.due || t.deadline) ? ` | ${t.due || t.deadline}` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
